import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Edit, Printer, RefreshCw, Download, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { Box, Menu, MenuItem } from '@mui/material';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { flattenFiltersForApi } from '../../utils/listFilters';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;

const formatAddress = (addr) => {
  if (!addr) return '-';
  const parts = [addr.Address, addr.Location?.Name, addr.Emirate?.Name].filter(Boolean);
  return parts.length ? parts.join(', ') : '-';
};

const formatDate = (d) => {
  if (d == null || d === '') return '-';
  let date;
  if (typeof d === 'object' && d.Day != null && d.Month != null && d.Year != null) {
    date = new Date(d.Year, d.Month - 1, d.Day);
  } else if (typeof d === 'string') {
    date = new Date(d);
  } else if (d instanceof Date) {
    date = d;
  } else {
    return '-';
  }
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const formatCurrency = (n) => {
  if (n == null || n === '') return '-';
  const num = typeof n === 'number' ? n : parseFloat(n);
  return Number.isNaN(num) ? '-' : `AED ${num.toFixed(2)}`;
};

const buildColumns = (ui) => [
  { field: 'voucherno', title: ui('columns.voucherNo'), widthClass: 'w-[6%]', hideable: true },
  { field: 'clientbillno', title: ui('columns.clientBillNo'), widthClass: 'w-[5%]', hideable: true },
  { field: 'batchno', title: ui('columns.batchNo'), widthClass: 'w-[5%]', hideable: true },
  { field: 'clientname', title: ui('columns.client'), widthClass: 'w-[8%]', hideable: true },
  { field: 'customername', title: ui('columns.customer'), widthClass: 'w-[8%]', hideable: true },
  { field: 'deliveryaddress', title: ui('columns.deliveryAddress'), widthClass: 'w-[10%]', hideable: true },
  { field: 'orderdate', title: ui('columns.orderDate'), widthClass: 'w-[6%]', hideable: true },
  { field: 'deliverydate', title: ui('columns.deliveryDate'), widthClass: 'w-[6%]', hideable: true },
  { field: 'productprice', title: ui('columns.product'), widthClass: 'w-[6%]', hideable: true },
  { field: 'deliverycharge', title: ui('columns.deliveryCh'), widthClass: 'w-[6%]', hideable: true },
  { field: 'total', title: ui('columns.total'), widthClass: 'w-[6%]', hideable: true },
  { field: 'status', title: ui('common.status'), widthClass: 'w-[6%]', hideable: true },
  { field: 'paytype', title: ui('columns.payType'), widthClass: 'w-[5%]', hideable: true },
  { field: 'actions', title: ui('columns.action'), widthClass: 'w-[8%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const defaultVisibility = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const getCellValue = (order, field) => {
  switch (field) {
    case 'voucherno':
      return order.VoucherNo || '-';
    case 'clientbillno':
      return order.ClientBillNo || '-';
    case 'batchno':
      return order.BatchNo || '-';
    case 'clientname':
      return order.Client?.Name || '-';
    case 'customername':
      return order.Customer?.Name || '-';
    case 'deliveryaddress':
      return formatAddress(order.CustomerAddress);
    case 'orderdate':
      return formatDate(order.Date);
    case 'deliverydate':
      return formatDate(order.DeliveryDate);
    case 'productprice':
      return formatCurrency(order.ProductPrice ?? order.ProductAmount);
    case 'deliverycharge':
      return formatCurrency(order.DeliveryCharge);
    case 'total':
      return formatCurrency(order.TotalAmount ?? order.Total ?? order.CustomerAmount);
    case 'status':
      return order.Status?.Name || '-';
    case 'paytype':
      return order.PayType || '-';
    default:
      return order[field];
  }
};

const OrderList = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const navigate = useNavigate();
  const ORDER_COLUMNS = useMemo(() => buildColumns(ui), [ui]);

  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [voucherNo, setVoucherNo] = useState('');
  const [keyword, setKeyword] = useState('');
  const [activeVoucherNo, setActiveVoucherNo] = useState('');
  const [activeKeyword, setActiveKeyword] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() => defaultVisibility(buildColumns(getAppUi('en'))));
  const [groupField, setGroupField] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [printingOrderId, setPrintingOrderId] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  useEffect(() => {
    setColumnVisibility((prev) => {
      const next = defaultVisibility(ORDER_COLUMNS);
      ORDER_COLUMNS.forEach((col) => {
        if (prev[col.field] !== undefined) next[col.field] = prev[col.field];
      });
      return next;
    });
  }, [ORDER_COLUMNS]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchOrders = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    voucher: voucherArg,
    search: searchArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
  } = {}) => {
    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const voucher = voucherArg !== undefined ? voucherArg : activeVoucherNo;
    const search = searchArg !== undefined ? searchArg : activeKeyword;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const seq = ++loadSeqRef.current;
    const isAll = size === 'all';

    try {
      setLoading(true);
      setError('');
      const body = {
        Skip: isAll ? 0 : (pageNum - 1) * Number(size),
        Take: isAll ? 0 : Number(size),
        VoucherNo: (voucher || '').trim() || null,
        Keyword: (search || '').trim() || null,
        SortField: sField || null,
        SortDir: sDir || 'asc',
        Filters: flattenFiltersForApi(filters),
      };

      const response = await MainService.GetOrderListList(body);
      if (seq !== loadSeqRef.current) return;
      const list = parseList(response);
      setOrders(list);
      setTotalCount(parseTotal(response, list.length));
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Error loading orders:', err);
      setError('Failed to load orders');
      setOrders([]);
      setTotalCount(0);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [page, pageSize, activeVoucherNo, activeKeyword, sortField, sortDirection, filtersByField, ui]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => ORDER_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [ORDER_COLUMNS, columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows: orders }];
    const mapped = orders.map((r) => ({ ...r, [groupField]: getCellValue(r, groupField) }));
    return groupRows(mapped, groupField);
  }, [orders, groupField]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const handleSearch = () => {
    const v = voucherNo.trim();
    const k = keyword.trim();
    setPage(1);
    setActiveVoucherNo(v);
    setActiveKeyword(k);
    fetchOrders({ page: 1, voucher: v, search: k });
  };

  const handleRefresh = () => {
    setPage(1);
    setVoucherNo('');
    setKeyword('');
    setActiveVoucherNo('');
    setActiveKeyword('');
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    fetchOrders({
      page: 1,
      voucher: '',
      search: '',
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
    }
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
    }
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchOrders({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchOrders({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchOrders({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = ORDER_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    const nextFilters = { ...filtersByField, [field]: filter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchOrders({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchOrders({ page: 1, filters: nextFilters });
  };

  const clearSortAndFilters = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setFiltersByField({});
    setSortField('');
    setSortDirection('asc');
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    setPage(1);
    if (prev && prev.pageSize !== pageSize) {
      setPageSize(prev.pageSize);
    }
    fetchOrders({
      page: 1,
      pageSize: prev?.pageSize ?? pageSize,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const exportToExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: 'Orders',
        fileName: `orders_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', ...ORDER_COLUMNS.filter((c) => c.field !== 'actions').map((c) => c.title)],
        groups: displayGroups,
        groupFieldTitle: ORDER_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (order, serial) => [
          serial,
          ...ORDER_COLUMNS.filter((c) => c.field !== 'actions').map((c) => getCellValue(order, c.field)),
        ],
      });
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      if (err?.message === 'No rows to export') {
        setError('No orders to export');
      } else {
        setError('Failed to export Excel file');
      }
    }
  };

  const handleContextMenu = (event, order) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setContextMenu(null);
    setSelectedOrder(null);
  };

  const handleView = (order) => {
    handleMenuClose();
    if (order?.Id) navigate(`/home/orders/${order.Id}`);
  };

  const handleEdit = (order) => {
    handleMenuClose();
    if (order?.Id) navigate(`/home/orders/create-order/${order.Id}`);
  };

  const handlePrint = async (order) => {
    handleMenuClose();
    if (!order?.Id) return;

    try {
      setPrintingOrderId(order.Id);
      setError('');
      const response = await MainService.PrintOrder(order.Id);
      const filePath = typeof response === 'string' ? response : (response?.data || response);

      if (filePath) {
        const fullUrl = String(filePath).startsWith('http')
          ? filePath
          : `${getApiBaseUrl()}ReportFiles/${filePath}`;
        window.open(fullUrl, '_blank');
      } else {
        setError('Print error');
      }
    } catch (err) {
      console.error('Print error:', err);
      setError('Print error');
    } finally {
      setPrintingOrderId(null);
    }
  };

  const renderCell = (order, column) => {
    if (column.field === 'status') {
      if (!order.Status) return '-';
      return (
        <span
          className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
          style={{
            backgroundColor: order.Status.Color ? `${order.Status.Color}20` : '#e5e7eb',
            color: order.Status.Color || '#374151',
          }}
        >
          {order.Status.Name}
        </span>
      );
    }
    if (column.field === 'clientname') {
      return (
        <>
          {order.Client?.Name || '-'}
          {order.Client?.MobileNo1 && <span className="block text-xs text-gray-400">{order.Client.MobileNo1}</span>}
        </>
      );
    }
    if (column.field === 'customername') {
      return (
        <>
          {order.Customer?.Name || '-'}
          {order.Customer?.MobileNo1 && <span className="block text-xs text-gray-400">{order.Customer.MobileNo1}</span>}
        </>
      );
    }
    if (column.field === 'deliveryaddress') {
      const text = formatAddress(order.CustomerAddress);
      return <span title={text}>{text}</span>;
    }
    if (column.field === 'actions') {
      return (
        <div className="inline-flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => handleView(order)}
            className="inline-flex items-center gap-1 text-primary hover:text-green-700 text-sm font-medium"
            title={ui('common.viewDetails')}
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleEdit(order)}
            className="inline-flex items-center gap-1 text-primary hover:text-green-700 text-sm font-medium"
            title={ui('common.edit')}
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handlePrint(order)}
            disabled={printingOrderId === order.Id}
            className="inline-flex items-center gap-1 text-primary hover:text-green-700 text-sm font-medium disabled:opacity-50"
            title={ui('common.print')}
          >
            <Printer className="w-4 h-4" />
          </button>
        </div>
      );
    }
    const alignRight = ['productprice', 'deliverycharge', 'total'].includes(column.field);
    return (
      <span className={alignRight ? 'block text-right' : undefined}>
        {getCellValue(order, column.field)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="flex-shrink-0 text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'orderList')}</h1>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.voucherNo')}</label>
            <input
              type="text"
              value={voucherNo}
              onChange={(e) => setVoucherNo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={ui('orders.orderList.voucherPlaceholder')}
              className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{ui('orders.orderList.keyword')}</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={ui('orders.orderList.keywordPlaceholder')}
              className="w-56 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
            />
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-primary/50 flex items-center gap-2 disabled:opacity-70"
          >
            <Search className="w-4 h-4" />
            {ui('common.search')}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-70"
            title={ui('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {ui('common.refresh')}
          </button>
          <button
            type="button"
            onClick={exportToExcel}
            disabled={orders.length === 0}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      <div className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="master-table-scroll relative overflow-x-auto" style={{ maxHeight: 560 }}>
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">{ui('common.loading') || 'Loading...'}</span>
              </div>
            </div>
          )}

          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${ORDER_COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button type="button" onClick={clearSortAndFilters} className="text-primary hover:underline">
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={ORDER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={handleUngroup}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="w-full min-w-[1400px] table-fixed">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="w-[4%] px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                {ORDER_COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={ORDER_COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={handleGroupBy}
                      onUngroup={handleUngroup}
                      align={column.align}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-8 text-center text-gray-500">
                    {ui('orders.orderList.noOrders')}
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return displayGroups.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-4 py-2">
                            <GroupHeaderLabel
                              title={ORDER_COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((order, index) => {
                      serial += 1;
                      block.push(
                        <tr
                          key={order.Id || `${group.key}-${index}`}
                          className="hover:bg-gray-50"
                          onContextMenu={(e) => handleContextMenu(e, order)}
                        >
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                            {groupField || pageSize === 'all' ? serial : (page - 1) * Number(pageSize) + serial}
                          </td>
                          {ORDER_COLUMNS.map((column) =>
                            columnVisibility[column.field] === false ? null : (
                              <td
                                key={column.field}
                                className={`px-4 py-2 text-sm ${
                                  column.field === 'voucherno' ? 'font-medium text-gray-900 whitespace-nowrap' : 'text-gray-600'
                                } ${column.field === 'actions' ? 'text-center whitespace-nowrap' : ''}`}
                              >
                                {renderCell(order, column)}
                              </td>
                            )
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <Menu
          open={contextMenu !== null}
          onClose={handleMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          PaperProps={{
            sx: {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              borderRadius: '0.5rem',
              minWidth: 150,
              mt: 0.5,
            },
          }}
        >
          <MenuItem onClick={() => handleView(selectedOrder)} sx={{ fontSize: '0.875rem', py: 1.5, '&:hover': { backgroundColor: '#f0f9f4' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Eye className="h-4 w-4" style={{ color: '#46955A' }} />
              <span>{ui('common.view')}</span>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleEdit(selectedOrder)} sx={{ fontSize: '0.875rem', py: 1.5, '&:hover': { backgroundColor: '#f0f9f4' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Edit className="h-4 w-4" style={{ color: '#46955A' }} />
              <span>{ui('common.edit')}</span>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handlePrint(selectedOrder)}
            disabled={printingOrderId === selectedOrder?.Id}
            sx={{ fontSize: '0.875rem', py: 1.5, '&:hover': { backgroundColor: '#f0f9f4' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Printer className="h-4 w-4" style={{ color: '#46955A' }} />
              <span>{ui('common.print')}</span>
            </Box>
          </MenuItem>
        </Menu>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0
                  ? ui('orders.orderList.noOrders')
                  : ui('orders.orderList.showingToOf', { from: rangeFrom, to: rangeTo, total: totalCount })}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField
                  ? ` · grouped by ${ORDER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}`
                  : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {ui('common.previous')}
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {ui('common.next')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderList;
